import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../hooks';
import { billingApi, organizationsApi } from '../services/api';
import ScrollHeader from '../components/common/ScrollHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import { 
  CalendarIcon, 
  CreditCardIcon, 
  UsersIcon, 
  ChartBarIcon, 
  ArrowPathIcon, 
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

interface Organization {
  org_id: number;
  name: string;
  status: string;
}

interface BillingInfo {
  org_id: number;
  org_name: string;
  billing_start_date: string | null;
  billing_cycle_day: number;
  last_credit_reset: string | null;
  next_reset_date: string | null;
  monthly_credit_usage: number;
  credit_limit: number | null;
  remaining_credits: number | null;
  current_month_usage: MonthlyUsage[];
}

interface MonthlyUsage {
  month_year: string;
  credits_used: number;
  credits_limit: number | null;
  reset_date: string | null;
  user_email?: string;
  user_name?: string;
}

const Billing: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [monthlyHistory, setMonthlyHistory] = useState<MonthlyUsage[]>([]);
  const [selectedMonthUsage, setSelectedMonthUsage] = useState<MonthlyUsage[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMonthUsage, setLoadingMonthUsage] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [chartPeriod, setChartPeriod] = useState('6m');

  // Billing update form state
  const [billingStartDate, setBillingStartDate] = useState('');
  const [billingCycleDay, setBillingCycleDay] = useState(1);

  // Generate month options for the last 12 months
  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthYear = date.toISOString().slice(0, 7); // YYYY-MM format
      const displayName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      months.push({ value: monthYear, label: displayName });
    }
    return months;
  };

  const monthOptions = generateMonthOptions();

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchOrganizations();
    } else if (user?.role === 'org_admin') {
      // For org admins, get their organization directly
      fetchUserOrganizations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedOrgId) {
      fetchBillingInfo();
      fetchMonthlyHistory();
      fetchChartData(chartPeriod);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedOrgId) {
      fetchChartData(chartPeriod);
    }
  }, [chartPeriod]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsApi.getOrganizations({});
      
      // The correct response structure is response.data.data
      const orgs = response.data.data || [];
      
      setOrganizations(orgs);
      if (orgs.length > 0) {
        setSelectedOrgId(orgs[0].org_id);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to fetch organizations');
    }
  };

  const fetchUserOrganizations = async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await organizationsApi.getOrganizations({});
      
      // For org admins, filter to only show organizations they admin
      const allOrgs = response.data.data || [];
      
      // For now, we'll show all orgs but in the future you might want to filter
      // based on the user's admin role in specific organizations
      setOrganizations(allOrgs);
      if (allOrgs.length > 0) {
        setSelectedOrgId(allOrgs[0].org_id);
      }
    } catch (err) {
      console.error('Error fetching user organizations:', err);
      setError('Failed to fetch organizations');
    }
  };

  const fetchBillingInfo = async () => {
    if (!selectedOrgId) return;
    
    setLoading(true);
    try {
      const response = await billingApi.getOrganizationBilling(selectedOrgId);
      setBillingInfo(response.data);
      
      // Set form values
      setBillingStartDate(response.data.billing_start_date || '');
      setBillingCycleDay(response.data.billing_cycle_day || 1);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch billing information');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyHistory = async () => {
    if (!selectedOrgId) return;
    
    setLoadingHistory(true);
    try {
      const response = await billingApi.getMonthlyUsageHistory(selectedOrgId, 12);
      setMonthlyHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch monthly history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchChartData = async (period: string = '6m') => {
    if (!selectedOrgId) return;
    
    setLoadingChart(true);
    try {
      const response = await billingApi.getUsageChart(selectedOrgId, period);
      setChartData(response.data);
    } catch (err) {
      console.error('Failed to fetch chart data:', err);
    } finally {
      setLoadingChart(false);
    }
  };

  const fetchMonthUsage = async (monthYear: string) => {
    if (!selectedOrgId || !monthYear) return;
    
    setLoadingMonthUsage(true);
    try {
      const response = await billingApi.getMonthUserUsage(selectedOrgId, monthYear);
      setSelectedMonthUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch month usage:', err);
    } finally {
      setLoadingMonthUsage(false);
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      fetchMonthUsage(selectedMonth);
    }
  }, [selectedMonth, monthlyHistory]);

  const handleUpdateBilling = async () => {
    if (!selectedOrgId) return;
    
    setLoading(true);
    try {
      await billingApi.updateOrganizationBilling(selectedOrgId, {
        billing_start_date: billingStartDate || undefined,
        billing_cycle_day: billingCycleDay
      });
      
      setSuccess('Billing cycle updated successfully');
      fetchBillingInfo();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update billing cycle');
    } finally {
      setLoading(false);
    }
  };

  const handleResetCredits = async () => {
    if (!selectedOrgId || !window.confirm('Are you sure you want to reset credits for this organization?')) return;
    
    setLoading(true);
    try {
      await billingApi.resetOrganizationCredits(selectedOrgId);
      setSuccess('Credits reset successfully');
      fetchBillingInfo();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset credits');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllCredits = async () => {
    if (!window.confirm('Are you sure you want to reset credits for ALL organizations? This action cannot be undone.')) return;
    
    setLoading(true);
    try {
      await billingApi.resetAllCredits();
      setSuccess('All credits reset successfully');
      fetchBillingInfo();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset all credits');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCredits = (credits: number | null) => {
    if (credits === null) return 'N/A';
    return credits.toLocaleString();
  };

  const getUsagePercentage = (used: number, limit: number | null) => {
    if (!limit) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageBadgeColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    if (percentage >= 75) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
  };

  if (!user || (user.role !== 'super_admin' && user.role !== 'org_admin')) {
    return (
      <div className="space-y-8">
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5" />
          Access denied. Only super administrators and organization administrators can access billing management.
        </div>
      </div>
    );
  }

  if (loading && !billingInfo) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="h-7 w-1/4 bg-light-hover dark:bg-dark-hover rounded-lg animate-pulse" />
          <div className="w-32 h-10 bg-light-hover dark:bg-dark-hover rounded-lg animate-pulse" />
        </div>
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
          <TableSkeleton rows={10} columns={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <ScrollHeader title="Billing Management" />
        {user?.role === 'super_admin' && (
          <div className="flex gap-2">
            <button
              onClick={handleResetAllCredits}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Reset All Credits
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-500">
          {success}
        </div>
      )}

      {/* Organization Selector - Only show for super admins */}
      {user?.role === 'super_admin' && (
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Organization</h3>
          <select
            value={selectedOrgId?.toString() || ''}
            onChange={(e) => setSelectedOrgId(parseInt(e.target.value))}
            className="w-full px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Select an organization</option>
            {organizations.map((org) => (
              <option key={org.org_id} value={org.org_id.toString()}>
                {org.name} (ID: {org.org_id})
              </option>
            ))}
          </select>
        </div>
      )}

      {billingInfo && (
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="border-b border-light-border dark:border-dark-border">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', name: 'Overview', icon: ChartBarIcon },
                ...(user?.role === 'super_admin' ? [{ id: 'settings', name: 'Billing Settings', icon: Cog6ToothIcon }] : []),
                { id: 'usage', name: 'Usage History', icon: CalendarIcon },
                { id: 'users', name: 'User Usage', icon: UsersIcon },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                  <div className="flex items-center">
                    <CreditCardIcon className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Credit Limit</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCredits(billingInfo.credit_limit)}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Remaining Credits</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCredits(billingInfo.remaining_credits)}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                  <div className="flex items-center">
                    <UsersIcon className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Monthly Usage</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCredits(billingInfo.monthly_credit_usage)}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                  <div className="flex items-center">
                    <CalendarIcon className="h-8 w-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Next Reset</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDate(billingInfo.next_reset_date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Trend Chart */}
              <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-5 h-5" />
                    Monthly Usage Trend
                  </h3>
                  <div className="flex gap-2">
                    {['3m', '6m', '1y', '2y'].map((period) => (
                      <button
                        key={period}
                        onClick={() => setChartPeriod(period)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${
                          chartPeriod === period
                            ? 'bg-primary-500 text-white'
                            : 'bg-light-hover dark:bg-dark-hover text-gray-600 dark:text-gray-400 hover:bg-light-border dark:hover:bg-dark-border'
                        }`}
                      >
                        {period.toUpperCase()}
                      </button>
                    ))}
                    {loadingChart && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                    )}
                  </div>
                </div>
                {loadingChart ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <div className="w-full">
                      {chartData.length > 0 ? (
                        <div className="space-y-4">
                          {/* Enhanced bar chart representation */}
                          <div className={`grid gap-2 ${chartData.length <= 6 ? 'grid-cols-1 md:grid-cols-6' : chartData.length <= 12 ? 'grid-cols-2 md:grid-cols-6 lg:grid-cols-12' : 'grid-cols-3 md:grid-cols-8 lg:grid-cols-12'}`}>
                            {chartData.reverse().map((data, index) => {
                              const maxUsage = Math.max(...chartData.map(d => d.total_credits));
                              const height = maxUsage > 0 ? (data.total_credits / maxUsage) * 100 : 0;
                              return (
                                <div key={data.month_year} className="text-center">
                                  <div className="h-32 flex items-end justify-center mb-2">
                                    <div 
                                      className="w-6 bg-primary-500 rounded-t-lg transition-all duration-300 hover:bg-primary-600 cursor-pointer"
                                      style={{ height: `${Math.max(height, 5)}%` }}
                                      title={`${data.month_year}: ${formatCredits(data.total_credits)} credits (${data.user_count} users)`}
                                    ></div>
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {data.month_year.split('-')[1]}/{data.month_year.split('-')[0].slice(2)}
                                  </div>
                                  <div className="text-xs font-medium text-gray-900 dark:text-white">
                                    {formatCredits(data.total_credits)}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-500">
                                    {data.user_count} users
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                            {chartPeriod === '3m' ? 'Last 3 months' : chartPeriod === '6m' ? 'Last 6 months' : chartPeriod === '1y' ? 'Last 12 months' : 'Last 24 months'} usage trend with multipliers applied
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 dark:text-gray-400">
                          No usage data available for the selected period
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Billing Information */}
              <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Billing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Organization</label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{billingInfo.org_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Billing Start Date</label>
                    <p className="text-lg text-gray-900 dark:text-white">{formatDate(billingInfo.billing_start_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Billing Cycle Day</label>
                    <p className="text-lg text-gray-900 dark:text-white">{billingInfo.billing_cycle_day}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Last Reset</label>
                    <p className="text-lg text-gray-900 dark:text-white">{formatDate(billingInfo.last_credit_reset)}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleResetCredits}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-light-border dark:border-dark-border text-gray-700 dark:text-gray-200 hover:bg-light-hover dark:hover:bg-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    Reset Organization Credits
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5" />
                Billing Cycle Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="billing-start-date" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Billing Start Date
                  </label>
                  <input
                    id="billing-start-date"
                    type="date"
                    value={billingStartDate}
                    onChange={(e) => setBillingStartDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="billing-cycle-day" className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Billing Cycle Day (1-31)
                  </label>
                  <input
                    id="billing-cycle-day"
                    type="number"
                    min="1"
                    max="31"
                    value={billingCycleDay}
                    onChange={(e) => setBillingCycleDay(parseInt(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={handleUpdateBilling}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Update Billing Settings
                </button>
              </div>
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
              <div className="p-6 border-b border-light-border dark:border-dark-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Monthly Usage History</h3>
                  {loadingHistory && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                  )}
                </div>
              </div>
              
              {loadingHistory ? (
                <div className="p-6">
                  <TableSkeleton rows={8} columns={5} />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-light-border dark:divide-dark-border">
                    <thead className="bg-light-hover dark:bg-dark-hover">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Month</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credits Used</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credits Limit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usage %</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reset Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-dark-card divide-y divide-light-border dark:divide-dark-border">
                      {monthlyHistory.length > 0 ? (
                        monthlyHistory.map((usage) => {
                          const percentage = getUsagePercentage(usage.credits_used, usage.credits_limit);
                          return (
                            <tr key={usage.month_year}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{usage.month_year}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatCredits(usage.credits_used)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatCredits(usage.credits_limit)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUsageBadgeColor(percentage)}`}>
                                  {percentage.toFixed(1)}%
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(usage.reset_date)}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                            No usage history available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              {/* Month Selector */}
              <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Month for User Usage</h3>
                <div className="flex gap-4 items-center">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Current Month</option>
                    {monthOptions.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  {loadingMonthUsage && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                  )}
                </div>
              </div>

              {/* User Usage Table */}
              <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
                <div className="p-6 border-b border-light-border dark:border-dark-border">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedMonth ? `User Usage for ${monthOptions.find(m => m.value === selectedMonth)?.label}` : 'Current Month User Usage'}
                    </h3>
                    {loading && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                    )}
                  </div>
                </div>
                
                {loadingMonthUsage ? (
                  <div className="p-6">
                    <TableSkeleton rows={5} columns={5} />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-light-border dark:divide-dark-border">
                      <thead className="bg-light-hover dark:bg-dark-hover">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credits Used</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credits Limit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usage %</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-dark-card divide-y divide-light-border dark:divide-dark-border">
                        {(selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).length > 0 ? (
                          (selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).map((usage, index) => {
                            const percentage = getUsagePercentage(usage.credits_used, usage.credits_limit);
                            return (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{usage.user_name || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{usage.user_email || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatCredits(usage.credits_used)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatCredits(usage.credits_limit)}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUsageBadgeColor(percentage)}`}>
                                    {percentage.toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                              {selectedMonth ? `No usage data found for ${monthOptions.find(m => m.value === selectedMonth)?.label}` : 'No user usage data available for current month'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Usage Summary */}
              {(selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).length > 0 && (
                <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {(selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).length}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Credits Used</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCredits((selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).reduce((sum, usage) => sum + usage.credits_used, 0))}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Usage</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCredits((selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).length > 0 
                          ? (selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).reduce((sum, usage) => sum + usage.credits_used, 0) / (selectedMonth ? selectedMonthUsage : billingInfo.current_month_usage).length 
                          : 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Billing;
